import { createRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useDocumentTitle } from "usehooks-ts";
import { z } from "zod";
import ContentFrame from "@/components/ContentFrame";
import InboxSidebar from "@/components/inbox/InboxSidebar";
import InboxSidebarSkeleton from "@/components/inbox/InboxSidebarSkeleton";
import TaskListInviteNotification from "@/components/inbox/TaskListInviteNotification";
import TaskListNotFoundCallback from "@/components/TaskListNotFoundCallback";
import TaskNotFoundFallback from "@/components/TaskNotFoundFallback";
import NotificationSkeleton from "@/components/task-form/NotificationSkeleton";
import TaskForm from "@/components/task-form/TaskForm";
import TaskFormSkeleton from "@/components/task-form/TaskFormSkeleton";
import { TaskQueryParamsProvider } from "@/components/task-table/TaskQueryParamsContext";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { useSessionQuery } from "@/queries/queries";
import { appRoute } from "./appRoute";

const inboxSearchSchema = z.object({
    task: z.coerce.number().optional(),
    taskList: z.coerce.number().optional(),
    notification: z.coerce.number().optional(),
    tab: z.string().optional(),
});

export const inboxRoute = createRoute({
    getParentRoute: () => appRoute,
    path: "/inbox",
    validateSearch: inboxSearchSchema,
    component: InboxPage,
});

export default function InboxPage() {
    useDocumentTitle(`Inbox - Orsane`);
    const { data: sessionData } = useSessionQuery();
    const {
        task: taskId,
        taskList: taskListId,
        notification: notificationId,
    } = inboxRoute.useSearch({
        select: (s) => ({
            task: s.task,
            taskList: s.taskList,
            notification: s.notification,
        }),
    });

    if (!sessionData) {
        return null;
    }

    return (
        <ContentFrame>
            <ResizablePanelGroup autoSaveId={`inbox-panel-group-${sessionData!.session.userId}`} direction="horizontal">
                <ResizablePanel defaultSize={35} minSize={20} className="relative">
                    <Suspense fallback={<InboxSidebarSkeleton />}>
                        <InboxSidebar />
                    </Suspense>
                </ResizablePanel>
                <ResizableHandle />
                <ResizablePanel defaultSize={65} className="bg-white">
                    <div className="mx-auto h-full">
                        {!!taskId && (
                            <TaskQueryParamsProvider value={{ type: "assigned-to-me", taskListId: undefined, completedTasks: "all" }}>
                                <ErrorBoundary FallbackComponent={TaskNotFoundFallback} resetKeys={[taskId]}>
                                    <Suspense fallback={<TaskFormSkeleton />}>
                                        <TaskForm key={taskId} taskId={taskId} showCloseButton={false} />
                                    </Suspense>
                                </ErrorBoundary>
                            </TaskQueryParamsProvider>
                        )}
                        {!!taskListId && !!notificationId && (
                            <ErrorBoundary FallbackComponent={TaskListNotFoundCallback} resetKeys={[taskListId]}>
                                <Suspense fallback={<NotificationSkeleton />}>
                                    <TaskListInviteNotification key={taskListId} notificationId={notificationId!} />
                                </Suspense>
                            </ErrorBoundary>
                        )}
                    </div>
                </ResizablePanel>
            </ResizablePanelGroup>
        </ContentFrame>
    );
}
